
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Share, Plus, Monitor, Smartphone, Tablet } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useIsMobile } from '@/hooks/use-mobile';
import pokerChip from '@/assets/poker-chip.png';

export const PWAInstallPrompt = () => {
  const { 
    isInstallable, 
    isAndroidInstallable, 
    isIOSInstallable, 
    isDesktopInstallable,
    installApp, 
    dismissPrompt 
  } = usePWAInstall();
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    // Check if device is tablet
    const checkIfTablet = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isTabletSize = (width >= 768 && width <= 1024) || (height >= 768 && height <= 1024);
      setIsTablet(isTabletSize);
    };

    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);

    return () => window.removeEventListener('resize', checkIfTablet);
  }, []);

  useEffect(() => {
    console.log('PWA Debug - PWAInstallPrompt State:', {
      isInstallable,
      isAndroidInstallable,
      isIOSInstallable,
      isDesktopInstallable,
      isMobile,
      isTablet,
      isDismissed
    });

    // Check if user has previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      console.log('PWA Debug - Prompt previously dismissed');
      setIsDismissed(true);
      return;
    }

    // Show prompt immediately if installable
    if (isInstallable && !isDismissed) {
      console.log('PWA Debug - Showing install prompt');
      setIsVisible(true);
    }
  }, [isInstallable, isMobile, isTablet, isDismissed, isAndroidInstallable, isIOSInstallable, isDesktopInstallable]);

  const handleInstall = async () => {
    console.log('PWA Debug - Install button clicked');
    const success = await installApp();
    if (success) {
      setIsVisible(false);
    }
  };

  const handleDismiss = () => {
    console.log('PWA Debug - Dismiss button clicked');
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
    dismissPrompt();
  };

  // Show if installable and not dismissed
  if (!isInstallable || !isVisible || isDismissed) {
    console.log('PWA Debug - Not showing prompt:', {
      isInstallable,
      isVisible,
      isDismissed
    });
    return null;
  }

  const getDeviceIcon = () => {
    if (isDesktopInstallable) return Monitor;
    if (isTablet) return Tablet;
    return Smartphone;
  };

  const DeviceIcon = getDeviceIcon();

  console.log('PWA Debug - Rendering install prompt');

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-fade-in">
      <div className={`bg-card border border-border rounded-lg shadow-lg mx-auto relative ${
        isTablet ? 'max-w-md' : 'max-w-sm'
      }`}>
        {/* Close button in top-right corner */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="absolute top-2 right-2 h-8 w-8 p-0 hover:bg-muted z-10"
        >
          <X className="w-4 h-4" />
        </Button>

        <div className="p-4 pr-12">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <img src={pokerChip} alt="SplitPot" className="w-10 h-10" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-foreground mb-2">
                Add SplitPot to {isDesktopInstallable ? 'Desktop' : isTablet ? 'Tablet' : 'Home Screen'}
              </h3>
              
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                Install our app for quick access to poker payouts and offline functionality
              </p>
              
              {/* Install button for Android/Desktop */}
              {(isAndroidInstallable || isDesktopInstallable) && (
                <Button
                  size="sm"
                  onClick={handleInstall}
                  className="w-full mb-4 h-10"
                >
                  <DeviceIcon className="w-4 h-4 mr-2" />
                  Install SplitPot
                </Button>
              )}
              
              {/* iOS specific instructions */}
              {isIOSInstallable && (
                <div className="text-sm text-muted-foreground space-y-3">
                  <div className="font-medium text-foreground mb-2">
                    To install on {isTablet ? 'iPad' : 'iPhone'}:
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
                      <span className="text-primary font-bold text-sm">1</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Share className="w-4 h-4 text-primary" />
                      <span>Tap the Share button</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
                      <span className="text-primary font-bold text-sm">2</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4 text-primary" />
                      <span>Select "Add to Home Screen"</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
